/* serialization.js — the save/load format (metres ⇄ backend mm) + native snapshot.
 * Ported from lhc-app.html: buildBackendDesign (~61755), layerOf (~61746),
 * getState (48494) / applyState (48520) shapes, and the persist body (~62091).
 *
 * Pure: pass state (placements/walls/wizard) + lib explicitly. The canonical persisted
 * `design` is { room, cabinets, _native } — geometry in mm, _native the full metre-scale
 * snapshot used to restore the configurator exactly.
 *
 * SNAPSHOT SHAPE CONTRACT (WI-3). The native snapshot must be key-for-key identical to
 * the iframe's getState() (lhc-app.html 48494–48518) — 13 keys, emitted in the SAME
 * insertion order so JSON.stringify round-trips byte-identically:
 *   productId, placements, fabricId, legId, pillowsOn, headrestsOn, legHeight,
 *   walls, currentStage, kitchenWizardSnap, wallOpenings, symbolPlacements, decor
 * fabricId/legId/pillowsOn/headrestsOn/legHeight are product-generic LEGACY fields
 * (sofa-era; lhc-app.html 29024–29025, 31044–31047). They MUST NOT be stripped — old
 * saves and the iframe's applyState() depend on them. Geometry is METRES throughout.
 *
 * UltraPresent consumption contract (frontend/src/studio/present/UltraPresent.jsx +
 * presentPoses.js — updated for the Present placement fix). Present derives cabinet
 * poses through the SAME engine chain normal 3D uses (catalogBridge.buildModuleLibrary
 * (blob) → pricing.effDef → scene3d/cabinetMath.buildCabinetSpecs → engine/placement.
 * cabinetWorldPos + rules.roleBaseY) and its wall meshes through scene3d/roomMath.
 * computeWallSolids (W1 ring prisms; door/window holes via { openings }). The snapshot
 * fields Present reads are the SAME native fields (plus the blob-derived lib), all in
 * metres — placements/walls pass through the engine builders whole, so the engine's
 * optional fields now flow too:
 *   native.walls            [] of { id, start:[x,z], end:[x,z], height, thickness?,
 *                                    type?/style?, center/radius/ccw (arcs),
 *                                    elevCuts/elevHoles/elevCircles? }
 *   native.placements       [] of { id, type, wallId?, absLeft?, island?/x/z,
 *                                    faceSign?, clrLeft?/clrRight?, config?
 *                                    (customWidthMm/HeightMm, position.elevation/
 *                                    angle/outset, hingeDir) }
 *   native.wallOpenings     [] of { wallId, type:'door'|'window', u, width, height, sill,
 *                                    hinge ('right'?), open (bool) }
 *   native.kitchenWizardSnap { front, shell, worktop, plinth, handle }  (via finishFor)
 *   native.decor            [] of { id, type, x, y, z, rot, locked? }  (locked: 3D lock, engine/decorLock.js)
 */
import { M, DEFAULT_WALL_HEIGHT } from "./constants";

/* Legacy product-generic defaults — the engine's boot values (lhc-app.html):
 *   selectedFabric = FABRICS[2] 'linen-03' (29024), selectedLeg = LEGS[0] 'metal-slim'
 *   (29025), pillowsOn = true (31044), headrestsOn = false (31045),
 *   DEFAULT_LEG_HEIGHT = 0.12 (31046). Valid-id lists mirror FABRICS (28993) /
 *   LEGS (29003) so readDesign can reproduce applyState's "unknown id → keep
 *   current" behaviour (48570–48573). */
export const DEFAULT_FABRIC_ID = "linen-03";
export const DEFAULT_LEG_ID = "metal-slim";
export const DEFAULT_LEG_HEIGHT = 0.12;
export const FABRIC_IDS = [
  "linen-01", "linen-02", "linen-03", "linen-04",
  "leather-co", "leather-ch", "velvet-ch", "velvet-sl",
];
export const LEG_IDS = ["metal-slim", "brass", "black", "copper", "walnut", "oak"];

/** layerOf(def): cabinet → backend layer. (~61746) */
export function layerOf(def) {
  if (def && def.role === "cabinet-wall") return "wall";
  if (def && def.role === "cabinet-tall") return "tall";
  return "base";
}

/** buildBackendDesign(walls, placements, lib): the mm-scale {room, cabinets} export. (~61755)
 *  Note: height_mm is fixed 2700 in the prototype (the WALL_HEIGHT global is phantom). */
export function buildBackendDesign(walls, placements, lib, effDef) {
  const wl = walls || [];
  const pl = placements || [];
  const roomWalls = wl
    .filter((w) => w && w.start && w.end)
    .map((w) => ({ id: w.id, x1: w.start[0] * M, y1: w.start[1] * M, x2: w.end[0] * M, y2: w.end[1] * M }));

  let width_mm = 0, depth_mm = 0;
  if (roomWalls.length) {
    const xs = [], ys = [];
    roomWalls.forEach((w) => { xs.push(w.x1, w.x2); ys.push(w.y1, w.y2); });
    width_mm = Math.round(Math.max(...xs) - Math.min(...xs));
    depth_mm = Math.round(Math.max(...ys) - Math.min(...ys));
  }
  // 2026-08-18 hardcode purge: the export used to claim 2700mm for EVERY
  // design regardless of the real walls. It now reports the tallest wall
  // (the room's actual ceiling), falling back to 2700 only for wall-less
  // legacy designs.
  const _hts = wl.map((w) => (w && Number(w.height)) || 0).filter((h) => h > 0.5);
  const height_mm = _hts.length ? Math.round(Math.max(..._hts) * M) : 2700;

  const cabs = pl
    .filter((p) => p && !p.island && p.wallId)
    .map((p) => {
      const def = (effDef ? effDef(p, lib) : lib[p.type]) || {};
      return {
        id: p.id,
        type: p.type,
        wall_id: p.wallId,
        offset_mm: Math.round((p.absLeft || 0) * M),
        width_mm: Math.round((def.width || 0) * M),
        depth_mm: Math.round((def.depth || 0) * M),
        height_mm: Math.round((def.height || 0) * M),
        layer: layerOf(def),
        subtype: def.subtype || null,
      };
    });

  return { room: { walls: roomWalls, width_mm, depth_mm, height_mm }, cabinets: cabs };
}

/** buildNativeSnapshot(state): the getState() shape — the metre-scale restore payload.
 *  (getState 48494–48518.) Key set AND insertion order match getState() exactly so the
 *  persisted _native JSON.stringify's byte-identically to an iframe save. Legacy fields
 *  fall back to the engine's boot values when the state doesn't carry them. */
export function buildNativeSnapshot(state) {
  const s = state || {};
  const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));
  return {
    productId: s.productId != null ? s.productId : null,
    placements: clone(s.placements || []),
    fabricId: s.fabricId != null ? s.fabricId : DEFAULT_FABRIC_ID,
    legId: s.legId != null ? s.legId : DEFAULT_LEG_ID,
    pillowsOn: s.pillowsOn != null ? !!s.pillowsOn : true,
    headrestsOn: s.headrestsOn != null ? !!s.headrestsOn : false,
    legHeight: s.legHeight != null ? s.legHeight : DEFAULT_LEG_HEIGHT,
    walls: clone(s.walls || []),
    currentStage: s.currentStage != null ? s.currentStage : null,
    kitchenWizardSnap: clone(s.kitchenWizard || null),
    wallOpenings: clone(s.wallOpenings || []),
    symbolPlacements: clone(s.symbolPlacements || []),
    decor: clone(s.decor || []),
    // Custom floor polygon (3CAD 'Pavimenti' editing) — emitted ONLY when the
    // user detached the floor from the auto-derived room loop, so legacy saves
    // stay byte-identical (same only-when-set discipline as wall.thickness).
    ...(s.floorPlan ? { floorPlan: clone(s.floorPlan) } : {}),
  };
}

/** Full persisted design: { ...backend geometry, _native: snapshot }. (~61854 persist) */
export function buildPersistDesign(state, lib, effDef) {
  const geo = buildBackendDesign(state.walls, state.placements, lib, effDef);
  return { ...geo, _native: buildNativeSnapshot(state) };
}

/** readDesign(design): restore a state from a saved design (prefers _native). Inverse of
 *  buildPersistDesign for the fields the engine needs to re-run. Legacy-field semantics
 *  mirror applyState (48570–48576): unknown fabric/leg ids keep the current (default)
 *  selection; when a _native snapshot exists, pillowsOn/headrestsOn coerce with `!!`
 *  (an old snapshot missing them restores as false, exactly like the iframe); with no
 *  _native at all the engine boot defaults stand. */
export function readDesign(design) {
  const d = design || {};
  const hasNative = !!d._native;
  const n = d._native || {};
  return {
    productId: n.productId || null,
    placements: n.placements || [],
    walls: n.walls || [],
    currentStage: n.currentStage || null,
    kitchenWizard: n.kitchenWizardSnap || null,
    wallOpenings: n.wallOpenings || [],
    symbolPlacements: n.symbolPlacements || [],
    decor: n.decor || [],
    floorPlan: n.floorPlan || null, // custom floor polygon; null ⇒ auto-derived room loop
    fabricId: FABRIC_IDS.indexOf(n.fabricId) !== -1 ? n.fabricId : DEFAULT_FABRIC_ID,
    legId: LEG_IDS.indexOf(n.legId) !== -1 ? n.legId : DEFAULT_LEG_ID,
    pillowsOn: hasNative ? !!n.pillowsOn : true,
    headrestsOn: hasNative ? !!n.headrestsOn : false,
    legHeight: n.legHeight !== undefined ? n.legHeight : DEFAULT_LEG_HEIGHT,
  };
}

export { DEFAULT_WALL_HEIGHT };
